import { IComponentOptions, IPromise, IQService, IScope, ITimeoutService } from "angular";
import { HotkeysProvider } from "angular-hotkeys";
import { StateParams, StateService } from "@uirouter/angularjs";
import { combineLatest, skip } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AssigneeActions, IAssigneesState, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController, IAccount } from "@gtmhub/core";
import { untilScopeDestroyed } from "@gtmhub/core/rxjs";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator } from "@gtmhub/error-handling";
import { InsightService } from "@gtmhub/insights";
import { SearchService } from "@gtmhub/search/services/search.service";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { handleOutsideMouseEvent } from "@gtmhub/shared/utils/close-on-mousedown";
import { INgRedux } from "@gtmhub/state-management";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { byDateFieldDesc } from "@gtmhub/util/sorting-utils";
import { IViewHistoryItemsGroupedReqParams, ViewHistoryItemGrouped, ViewHistoryService } from "@gtmhub/view-history";
import { TrackingMetadata } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { FeatureModuleService } from "@webapp/feature-toggles/services/feature-module.service";
import { ISearchParams } from "@webapp/search/models/search-api.models";
import { SearchCollection } from "@webapp/search/models/search.models";
import dayjs from "@webapp/shared/libs/dayjs";
import { GlobalSearchFacetToggleOptionType, GlobalSearchItem, GlobalSearchMode, GlobalSearchRecentItemsFacetToggleOptionType } from "../../models/global-search";
import { GlobalSearchItemEnriched } from "../../models/global-search-item";
import { GlobalSearchService } from "../../services/global-search-service";
import {
  getItemReturnState,
  isEqualTo,
  onSearchItemHover,
  searchItemsToEnrichedGlobalSearchItemCollection,
  selectElementOnUpDownArrowPress,
} from "../../utils/global-search";
import {
  getRecentItemReturnState,
  getViewHistoryRejectedIdsByCurrentState,
  globalSearchFacetToRecentItemsFacetMap,
  recentItemsTargetTypesByRecentItemsFacetMap,
  searchCollectionTypeToUXCustomizationType,
  viewHistoryItemCollectionByRecentItemsFacetMap,
  viewHistoryItemsToEnrichedGlobalSearchItemCollection,
} from "../../utils/view-history";
import { InsightRedirectModalService } from "../insight-redirect-modal/insight-redirect-modal";

type ActiveSearchMode = "open" | "closed";

const SECTIONS_SHOWN = 5;
const ITEMS_SHOWN_IF_CURRENT_FACET = 10;
const ITEMS_SHOWN_IF_NOT_CURRENT_FACET = 4;
const TOP_SCORE = 1;
const DEFAULT_SCORE = 0;

interface IGlobalSearchRedux {
  assigneesState: IAssigneesState;
  assigneesMap: Record<string, Assignee>;
}

const bindStateToScope = (state: IAssigneesStoreState): IGlobalSearchRedux => ({
  assigneesState: state.assignees,
  assigneesMap: state.assignees.map,
});

interface ISearchScope extends IScope, IExtendedRootScopeService {}

interface IGlobalSearchCtrlBindings {
  currentFacet: string;
  activeUsersOnly: boolean;
  activeTeamsOnly: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface GlobalSearchCtrl extends IGlobalSearchCtrlBindings {}
export class GlobalSearchCtrl extends GtmhubController {
  facetsOrder: string[];
  recentItemsFacetOrder: string[];
  facetToggleOptionsArray: string[];
  currentRecentItemsFacets: GlobalSearchRecentItemsFacetToggleOptionType[];
  isEqualTo: <T>(compared: T, ...args: T[]) => boolean;
  onItemHover: ($event: JQueryEventObject, { hovered }: { hovered: boolean }) => void;
  // ---- Const values ---- //
  isOpen = false;
  shouldDisplayRecentItemsError = false;
  recentPrefix = "recent-";
  selectedElementClassName = "selected-item";
  listElementClassName = "item";
  itemsShownIfCurrentFacet = ITEMS_SHOWN_IF_CURRENT_FACET;
  // ---- Helpers ---- //
  searchTerm: string;
  itemsLimit = {};
  scoreMap: { [facetName: string]: number };
  assigneesMap: Record<string, Assignee>;
  indicators: {
    gettingSearchData?: IIndicator;
    loadingMore?: IIndicator;
  };
  itemsCount: { [facetName: string]: number } = {};
  itemsPerSection: number;
  account: IAccount;
  lastSearchId: string;
  searchIdTimer: number;
  lastRecentId: string;
  recentIdTimer: number;
  isAnyRecentItemCollectionNotEmpty: boolean;

  mode: GlobalSearchMode = "closed";
  hiddenFacets = new Set<string>();

  private facetToggleOptions: { [facetName: string]: string };
  private recentItemsFacetToggleOptions: Record<GlobalSearchRecentItemsFacetToggleOptionType, string>;
  private initialized = false;
  private assigneesState: IAssigneesState;

  public static $inject = [
    "$scope",
    "SearchService",
    "GlobalSearchService",
    "$state",
    "$q",
    "$stateParams",
    "InsightService",
    "AnalyticsService",
    "AccountResolverService",
    "hotkeys",
    "AssigneeActions",
    "$ngRedux",
    "$rootScope",
    "InsightRedirectModalService",
    "ViewHistoryService",
    "$timeout",
    "FeatureModuleService",
  ];

  constructor(
    private $scope: ISearchScope,
    private searchService: SearchService,
    private globalSearchService: GlobalSearchService,
    private $state: StateService,
    private $q: IQService,
    private $stateParams: StateParams,
    private insightService: InsightService,
    private analyticsService: AnalyticsService,
    private accountResolverService: AccountResolverService,
    private hotkeys: HotkeysProvider,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux,
    private $rootScope: ITraceRootScopeService,
    private insightRedirectModalService: InsightRedirectModalService,
    private viewHistoryService: ViewHistoryService,
    private $timeout: ITimeoutService,
    private featureModuleService: FeatureModuleService
  ) {
    super();

    this.indicators = {};

    this.adjustVisibleFacetsBasedOnEnabledFeatures();

    const searchContainerClasses = ["#global-search > .search-actions", ".search-results"];
    this.onDestroy(handleOutsideMouseEvent("mousedown", () => this.toggleActiveSearch("closed"), ...searchContainerClasses));
  }

  initSearch(): IPromise<void> {
    if (!this.initialized) {
      this.initialized = true;

      const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this);
      this.$scope.$on("$destroy", unsubscribe);

      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
      return this.$scope
        .watchUntilAndRejectOnError(
          () => this.assigneesState.isFetched,
          () => this.assigneesState.error
        )
        .then(() => this.init());
    }
  }

  toggleActiveSearch = (mode: ActiveSearchMode): void => {
    if (this.mode === mode) return;

    if (mode === "open") {
      this.$rootScope.traceAction("gs__focused_search_field", () => {
        this.changeMode("open");
        this.$timeout(() => this.focusSearchField());
      });
      return;
    }

    this.changeMode("closed");
  };

  shouldDisplayRecentItems(): boolean {
    return !!(
      !this.searchTerm &&
      this.isAnyRecentItemCollectionNotEmpty &&
      !this.shouldDisplayRecentItemsError &&
      (!this.indicators.gettingSearchData || this.indicators.loadingMore)
    );
  }

  private adjustVisibleFacetsBasedOnEnabledFeatures() {
    // eslint-disable-next-line @foxglove/no-boolean-parameters
    const toggleHiddenFacet = (facet: string, hidden: boolean) => {
      if (hidden) {
        this.hiddenFacets.add(facet);
      } else {
        this.hiddenFacets.delete(facet);
      }
    };

    combineLatest([
      this.featureModuleService.isKpiModuleEnabled$(),
      this.featureModuleService.isTaskModuleEnabled$(),
      this.featureModuleService.isWhiteboardModuleEnabled$(),
    ])
      .pipe(
        skip(1), // we don't need the first emit but rather when any of the future features changes
        untilScopeDestroyed(this.$scope)
      )
      .subscribe(([isKpiModuleEnabled, isTaskModuleEnabled, isWhiteboardModuleEnabled]) => {
        toggleHiddenFacet("kpis", !isKpiModuleEnabled);
        toggleHiddenFacet("tasks", !isTaskModuleEnabled);
        toggleHiddenFacet("whiteboards", !isWhiteboardModuleEnabled);
        this.$scope.$evalAsync();
      });
  }

  private init(): IPromise<void> {
    this.account = this.accountResolverService.getAccountData();
    this.isEqualTo = isEqualTo;
    this.onItemHover = onSearchItemHover;
    this.configureHotkeys();

    return this.$q
      .all({
        facetToggleOptions: this.globalSearchService.getFacetToggleOptions(),
        recentItemsFacetToggleOptions: this.globalSearchService.getRecentItemsFacetToggleOptions(),
      })
      .then(({ facetToggleOptions, recentItemsFacetToggleOptions }) => {
        this.facetToggleOptions = facetToggleOptions;
        this.recentItemsFacetToggleOptions = recentItemsFacetToggleOptions;
        this.facetToggleOptionsArray = Object.values(this.facetToggleOptions).filter(
          (value) => [this.facetToggleOptions.organization, this.facetToggleOptions.insightsAndInsightboards].indexOf(value) === -1
        );
        this.facetsOrder = Object.keys(this.facetToggleOptions).filter((key) => key !== "everywhere");
        this.recentItemsFacetOrder = Object.keys(this.recentItemsFacetToggleOptions);
        this.scoreMap = this.facetsOrder.reduce((map, facet) => {
          map[facet] = DEFAULT_SCORE;
          return map;
        }, {});

        this.currentFacet = this.currentFacet && this.facetsOrder.includes(this.currentFacet) ? this.currentFacet : "everywhere";
        this.currentRecentItemsFacets = globalSearchFacetToRecentItemsFacetMap[this.currentFacet];
        this.getRecentItems();
      });
  }

  onFacetChange($event: MouseEvent, facet: string, searchTerm: string): void {
    this.$rootScope.traceAction("gs__changed_facet", () => {
      this.currentFacet = Object.keys(this.facetToggleOptions).find((key) => this.facetToggleOptions[key] === facet);
      this.currentRecentItemsFacets = globalSearchFacetToRecentItemsFacetMap[this.currentFacet];
      this.itemsLimit = this.updateItemsLimit();

      if (searchTerm) {
        this.getSearchData(searchTerm);
      } else {
        this.getRecentItems();
      }
    });
  }

  onSearchTermChange(searchTerm: string): void {
    this.$rootScope.traceAction("gs__changed_search_term", () => {
      if (this.facetToggleOptions) {
        this.changeMode("open");

        if (searchTerm) {
          this.getSearchData(searchTerm);
        } else {
          this.getRecentItems();
        }
      } else {
        this.initSearch();
      }
    });
  }

  viewMoreResults(facet: string, searchTerm: string): void {
    this.$rootScope.traceAction("gs__view_more_results", () => {
      this.sendSearchAnalyticsData("View more clicked");

      this.currentFacet = facet;
      this.currentRecentItemsFacets = globalSearchFacetToRecentItemsFacetMap[this.currentFacet];
      this.itemsLimit = this.updateItemsLimit();
      this.getSearchData(searchTerm, { loadMore: true });
    });
  }

  viewMoreRecentItemResults(facet: GlobalSearchRecentItemsFacetToggleOptionType): void {
    this.$rootScope.traceAction("gs__view_more_recent_items", () => {
      this.sendRecentItemsAnalyticsData("View More Recent Items Clicked");

      if (facet !== "otherItems") {
        this.currentFacet = facet;
        this.currentRecentItemsFacets = globalSearchFacetToRecentItemsFacetMap[this.currentFacet];
        this.itemsLimit = this.updateItemsLimit();
      } else {
        this.currentRecentItemsFacets = [facet];
      }
      this.getRecentItems({ loadMore: true });
    });
  }

  viewAllResults(facet: string, searchTerm: string): void {
    this.sendSearchAnalyticsData("View all clicked");
    this.$state.go("gtmhub.globalSearch", { facet, searchTerm: searchTerm });
  }

  generateHrefLink(facet: string, item: GlobalSearchItemEnriched<SearchCollection>): string {
    if (facet === "insights" || (facet === "insightsAndInsightboards" && item.collectionName === "insights")) {
      return "";
    }

    const itemReturnState = getItemReturnState(facet, item);
    return this.$state.href(itemReturnState.state, itemReturnState.stateParams);
  }

  generateRecentItemHrefLink(facet: Exclude<GlobalSearchRecentItemsFacetToggleOptionType, "insights">, item: GlobalSearchItemEnriched<SearchCollection>): string {
    if (item.collectionName === "insights") {
      return "";
    }

    const itemReturnState = getRecentItemReturnState(facet, item);
    return this.$state.href(itemReturnState.state, itemReturnState.stateParams);
  }

  selectItem(facet: string, item: GlobalSearchItemEnriched<SearchCollection>, analyticsData: { facetIdx: number; itemIdx: number }): void {
    if (item.isRecent) {
      this.sendRecentItemTrackEvent(analyticsData);
    } else {
      this.sendSearchTrackEvent(analyticsData);
    }

    if (item.collectionName === "insights" && (facet === "insights" || facet === "insightsAndInsightboards")) {
      this.openInsightRedirectModal(item as GlobalSearchItemEnriched<"insights">);
    }

    this.addItemToViewHistory(item);
  }

  filterFacets($ctrl: GlobalSearchCtrl): (facet: GlobalSearchFacetToggleOptionType) => boolean {
    return (facet: GlobalSearchFacetToggleOptionType) => {
      if (!$ctrl[facet] || !$ctrl[facet].length) return false;

      if ($ctrl.currentFacet === "everywhere") {
        const facetsToExclude: GlobalSearchFacetToggleOptionType[] = ["insights", "insightboards", "employees", "teams"];
        return facetsToExclude.indexOf(facet) === -1;
      }

      return facet === $ctrl.currentFacet;
    };
  }

  filterRecentItemFacets($ctrl: GlobalSearchCtrl): (facet: GlobalSearchRecentItemsFacetToggleOptionType) => boolean {
    return (facet: GlobalSearchRecentItemsFacetToggleOptionType) => {
      if (!$ctrl["recent-" + facet] || !$ctrl["recent-" + facet].length) return false;

      return this.currentRecentItemsFacets.includes(facet);
    };
  }

  updateFacetsOrder(scoreMap: { [facetName: string]: number }): (facet: string) => number {
    return (facet: string): number => -scoreMap[facet];
  }

  showMoreResults(facet: string, { compound }: { compound?: boolean } = {}): boolean {
    return (
      !isEqualTo(this.currentFacet, facet) &&
      this.itemsCount[facet] > ITEMS_SHOWN_IF_NOT_CURRENT_FACET &&
      (compound ? isEqualTo(facet, "organization", "insightsAndInsightboards") : !isEqualTo(facet, "organization", "insightsAndInsightboards"))
    );
  }

  showMoreRecentItemResults(facet: GlobalSearchRecentItemsFacetToggleOptionType, { compound }: { compound?: boolean } = {}): boolean {
    return (
      !isEqualTo(this.currentFacet, ...this.currentRecentItemsFacets) &&
      this.currentRecentItemsFacets.length > 1 &&
      this.itemsCount["recent-" + facet] > ITEMS_SHOWN_IF_NOT_CURRENT_FACET &&
      (compound ? isEqualTo(facet, "employeesAndTeams") : !isEqualTo(facet, "employeesAndTeams"))
    );
  }

  onKeydown($event: JQueryEventObject): void {
    const isKeyCodeEnter = $event.keyCode === 13;
    if (isKeyCodeEnter) {
      const selectedEl = document.querySelector<HTMLElement>(".selected-item");
      if (!selectedEl || !selectedEl.classList.contains("item")) {
        this.onSearchTermChange(this.searchTerm);
      } else {
        selectedEl.click();
      }

      return;
    }

    const isKeyCodeUp = $event.keyCode === 38;
    const isKeyCodeDown = $event.keyCode === 40;
    if (isKeyCodeUp || isKeyCodeDown) {
      selectElementOnUpDownArrowPress({
        $event,
        direction: isKeyCodeUp ? "up" : "down",
        itemsPerSection: this.itemsPerSection,
        sectionsShown: SECTIONS_SHOWN,
        selectedElementClassName: this.selectedElementClassName,
        listElementClassName: this.listElementClassName,
      });
    }
  }

  private getSearchData(searchTerm: string, options: { loadMore: boolean } = { loadMore: false }): void {
    this.indicators = !options.loadMore
      ? {
          gettingSearchData: { progress: true },
        }
      : {
          loadingMore: { progress: true },
        };
    this.isOpen = true;
    this.shouldDisplayRecentItemsError = false;
    // in case the user does not have access to initial facet
    // update value here because bindings do not update on init
    this.currentFacet = this.facetsOrder.includes(this.currentFacet) ? this.currentFacet : "everywhere";
    this.currentRecentItemsFacets = globalSearchFacetToRecentItemsFacetMap[this.currentFacet];

    this.focusSearchField();
    this.updateSearchId();
    this.sendSearchAnalyticsData("Item searched");

    if (this.currentFacet === "everywhere") {
      this.itemsPerSection = ITEMS_SHOWN_IF_NOT_CURRENT_FACET;
      this.getAllSearchCollections(searchTerm);
    } else {
      this.itemsPerSection = ITEMS_SHOWN_IF_CURRENT_FACET;
      // Move current facet to top
      this.resetScoreMap();
      this.scoreMap[this.currentFacet] = TOP_SCORE;

      this.getSingleSearchCollection(searchTerm, this.currentFacet);
    }
  }

  private focusSearchField(): void {
    this.$scope.$broadcast("focusGlobalSearchField");
  }

  private addItemToViewHistory(item: GlobalSearchItemEnriched<SearchCollection>): void {
    this.viewHistoryService.addItemToViewHistory(
      this.viewHistoryService.payloadBuilder.withItem(item).forType(searchCollectionTypeToUXCustomizationType[item.collectionName]).forSource("search").generate()
    );
  }

  private getSingleSearchCollection(searchTerm: string, currentFacetName: string): void {
    const queryParams: ISearchParams = {
      skip: 0,
      take: ITEMS_SHOWN_IF_CURRENT_FACET,
    };

    const searchBody = this.globalSearchService.getSearchBody(searchTerm, currentFacetName, {
      activeUsersOnly: this.activeUsersOnly,
      activeTeamsOnly: this.activeTeamsOnly,
    });

    this.searchService.getSomeSearchData<GlobalSearchItem<SearchCollection>>(searchBody, queryParams).then(
      (searchResults) => {
        this[currentFacetName] = searchItemsToEnrichedGlobalSearchItemCollection(currentFacetName, searchResults.items);
        this.itemsCount[currentFacetName] = searchResults.totalCount;

        delete this.indicators.gettingSearchData;
        delete this.indicators.loadingMore;
        this.sendSearchAnalyticsData("Results shown");
      },
      (error) => (this.indicators.gettingSearchData ? (this.indicators.gettingSearchData = { error }) : (this.indicators.loadingMore = { error }))
    );
  }

  private getSingleRecentItemsCollection(recentItemsFacet: GlobalSearchRecentItemsFacetToggleOptionType): IPromise<number> {
    const itemsPerSectionIfNotInStateView =
      this.currentRecentItemsFacets.length === 1 && this.currentRecentItemsFacets[0] === recentItemsFacet
        ? ITEMS_SHOWN_IF_CURRENT_FACET
        : ITEMS_SHOWN_IF_NOT_CURRENT_FACET;

    this.itemsPerSection = itemsPerSectionIfNotInStateView;

    const recentItemsMongoQuery: IViewHistoryItemsGroupedReqParams = {
      filter: {
        $and: [
          {
            type: {
              $in: recentItemsTargetTypesByRecentItemsFacetMap[recentItemsFacet],
            },
          },
          {
            source: {
              $in: ["search"],
            },
          },
        ],
      },
      rejectedIds: getViewHistoryRejectedIdsByCurrentState(this.$state.current.name, this.$stateParams),
      skip: 0,
      limit: this.itemsPerSection,
    };

    return this.viewHistoryService.getViewHistoryItems(recentItemsMongoQuery).then((recentSearchItems) => {
      let recentItemsCollection: ViewHistoryItemGrouped[] = [];
      viewHistoryItemCollectionByRecentItemsFacetMap[recentItemsFacet].forEach((collection) => {
        recentItemsCollection = recentItemsCollection.concat(recentSearchItems[collection] || []);
      });
      this["recent-" + recentItemsFacet] = viewHistoryItemsToEnrichedGlobalSearchItemCollection(
        recentItemsFacet,
        recentItemsCollection.sort(byDateFieldDesc("lastVisited"))
      );
      this.itemsCount["recent-" + recentItemsFacet] = recentSearchItems.totalCount;

      return (recentSearchItems && recentSearchItems.totalCount) || 0;
    });
  }

  private changeMode(mode: ActiveSearchMode): void {
    this.mode = mode;
    this.isOpen = mode !== "closed";
    this.$scope.$evalAsync();
  }

  private resetScoreMap(): void {
    for (const key in this.scoreMap) {
      this.scoreMap[key] = DEFAULT_SCORE;
    }
  }

  private getRecentItems(options: { loadMore: boolean } = { loadMore: false }): void {
    this.isOpen = false;
    this.indicators = !options.loadMore
      ? {
          gettingSearchData: { progress: true },
        }
      : {
          loadingMore: { progress: true },
        };
    this.shouldDisplayRecentItemsError = false;
    // in case the user does not have access to initial facet
    // update value here because bindings do not update on init
    if (!options.loadMore) {
      this.currentFacet = this.facetsOrder.includes(this.currentFacet) ? this.currentFacet : "everywhere";
      this.currentRecentItemsFacets = globalSearchFacetToRecentItemsFacetMap[this.currentFacet];
    }

    this.updateRecentId();
    this.sendRecentItemsAnalyticsData("Recent Item Searched");

    let isRecentItemsPromiseResolved = false;
    const delayBuffer = 400;
    this.$timeout(() => {
      if (!isRecentItemsPromiseResolved) {
        this.isOpen = true;
      }
    }, delayBuffer);
    const recentItemPromises = this.currentRecentItemsFacets.map((facet) => this.getSingleRecentItemsCollection(facet));

    this.$q.all(recentItemPromises).then(
      (recentItemCountColl) => {
        isRecentItemsPromiseResolved = true;

        delete this.indicators.gettingSearchData;
        delete this.indicators.loadingMore;

        this.isAnyRecentItemCollectionNotEmpty = (recentItemCountColl || []).some((count) => 0 < count);
        this.isOpen = this.isAnyRecentItemCollectionNotEmpty;
        this.sendRecentItemsAnalyticsData("Recent Item Results Shown");
      },
      () => {
        isRecentItemsPromiseResolved = true;

        delete this.indicators.gettingSearchData;
        delete this.indicators.loadingMore;
        this.shouldDisplayRecentItemsError = true;
        this.isOpen = true;
      }
    );
  }

  private getAllSearchCollections(searchTerm: string): void {
    const searchedCollections: string[] = [
      "okrs",
      "sessions",
      "tasks",
      "organization",
      "filters",
      "lists",
      "insightsAndInsightboards",
      "kpis",
      "automations",
      "whiteboards",
    ].filter((collection) => this.facetsOrder.includes(collection));

    const searchBody = this.globalSearchService.getSearchBodyV2(searchTerm, searchedCollections, {
      activeUsersOnly: this.activeUsersOnly,
      activeTeamsOnly: this.activeTeamsOnly,
    });
    const queryParams: ISearchParams = {
      skip: 0,
      take: ITEMS_SHOWN_IF_NOT_CURRENT_FACET,
    };

    this.searchService.getSearchDataV2(searchBody, queryParams).then((result) => {
      searchedCollections.forEach((collectionName) => {
        const searchResult = result.items[collectionName];
        if (searchResult) {
          this[collectionName] = searchItemsToEnrichedGlobalSearchItemCollection(collectionName, searchResult.items);
          this.itemsCount[collectionName] = searchResult.totalCount;
          this.scoreMap[collectionName] = searchResult.maxScore || 1;
        }
      });
      delete this.indicators.gettingSearchData;
      this.sendSearchAnalyticsData("Results shown");
    });
  }

  private sendSearchTrackEvent(analyticsData: { facetIdx: number; itemIdx: number }): void {
    const metaItemFound = this.calculateSearchMeta({
      facet: this.currentFacet,
      facetIdx: analyticsData.facetIdx,
      itemIdx: analyticsData.itemIdx,
      page: 1,
      itemsPerPage: this.itemsPerSection,
    });
    this.sendSearchAnalyticsData("Item clicked", metaItemFound);
  }

  private sendRecentItemTrackEvent(analyticsData: { facetIdx: number; itemIdx: number }): void {
    const metaItemFound = this.calculateRecentItemMeta({
      facet: this.currentFacet,
      facetIdx: analyticsData.facetIdx,
      itemIdx: analyticsData.itemIdx,
      itemsPerPage: this.itemsPerSection,
    });
    this.sendRecentItemsAnalyticsData("Recent Item Clicked", metaItemFound);
  }

  private openInsightRedirectModal(item: GlobalSearchItemEnriched<"insights">): void {
    this.insightService.getInsightSafe(item.name).then((insight) => {
      this.insightService.getUsageOnBoards(insight.id).then((boardUsage) => {
        this.insightRedirectModalService.open({ insight, boardUsage });
      });
    });
  }

  private updateItemsLimit(): { [facetName: string]: number } {
    return this.facetsOrder.reduce((acc, facet) => {
      if (this.currentFacet === facet) {
        acc[facet] = ITEMS_SHOWN_IF_CURRENT_FACET;
      } else {
        acc[facet] = ITEMS_SHOWN_IF_NOT_CURRENT_FACET;
      }

      return acc;
    }, {});
  }

  // when input field is not focused
  private configureHotkeys(): void {
    this.hotkeys.add({
      combo: "up",
      callback: ($event) => {
        selectElementOnUpDownArrowPress({
          $event,
          direction: "up",
          itemsPerSection: this.itemsPerSection,
          sectionsShown: SECTIONS_SHOWN,
          selectedElementClassName: this.selectedElementClassName,
          listElementClassName: this.listElementClassName,
        });
      },
    });

    this.hotkeys.add({
      combo: "down",
      callback: ($event) => {
        selectElementOnUpDownArrowPress({
          $event,
          direction: "down",
          itemsPerSection: this.itemsPerSection,
          sectionsShown: SECTIONS_SHOWN,
          selectedElementClassName: this.selectedElementClassName,
          listElementClassName: this.listElementClassName,
        });
      },
    });
  }

  private sendSearchAnalyticsData(eventName: string, meta: TrackingMetadata = {}): void {
    const enrichedMeta = {
      ...meta,
      account_type: this.account.type,
      state_view: false,
      search_id: dayjs.utc().isBefore(this.searchIdTimer) ? this.lastSearchId : this.updateSearchId(),
      facet: this.currentFacet,
    };
    this.analyticsService.track(eventName, enrichedMeta);
  }

  private sendRecentItemsAnalyticsData(eventName: string, meta: TrackingMetadata = {}): void {
    const enrichedMeta = {
      ...meta,
      account_type: this.account.type,
      state_view: false,
      search_id: dayjs.utc().isBefore(this.recentIdTimer) ? this.lastRecentId : this.updateRecentId(),
      facet: this.currentFacet,
    };
    this.analyticsService.track(eventName, enrichedMeta);
  }

  private calculateSearchMeta(metaItemFound: { facet: string; facetIdx: number; itemIdx: number; page: number; itemsPerPage: number }) {
    return {
      ...(metaItemFound.facet === "everywhere" && { facetPosition: metaItemFound.facetIdx + 1 }),
      itemPosition: (metaItemFound.page - 1) * metaItemFound.itemsPerPage + metaItemFound.itemIdx + 1,
    };
  }

  private calculateRecentItemMeta(metaItemFound: { facet: string; facetIdx: number; itemIdx: number; itemsPerPage: number }) {
    return {
      ...(metaItemFound.facet === "everywhere" && { facetPosition: metaItemFound.facetIdx + 1 }),
      itemPosition: metaItemFound.itemIdx + 1,
    };
  }

  private updateSearchId(): string {
    this.searchIdTimer = dayjs.utc().add(1, "minutes").valueOf();
    this.lastSearchId = uuidv4();
    return this.lastSearchId;
  }

  private updateRecentId(): string {
    this.recentIdTimer = dayjs.utc().add(1, "minutes").valueOf();
    this.lastRecentId = uuidv4();
    return this.lastRecentId;
  }
}

export const GlobalSearchComponent: IComponentOptions = {
  template: require("./global-search.html"),
  controller: GlobalSearchCtrl,
  bindings: {
    currentFacet: "@",
    activeUsersOnly: "<",
    activeTeamsOnly: "<",
  },
};
